import { Component, inject } from '@angular/core';

import { NavigationService } from '@frontend/vanilla/core';
import { AuthDetailsStore } from '@payments/common-utils';

import { AppConstants, Config, InputType, MOBILE_APP_CHANNELS, ProductType } from '../config/appConstants';
import { UrlConstants } from '../config/urlConstants';
import { QDInstrument } from '../model/qdInstrument.model';
import { BehaviorDataService } from '../service/behaviorData.service';
import { DataService } from '../service/data.service';
import { DepositService } from '../service/deposit.service';
import { GeocomplyService } from '../service/geocomply.service';
import { GtmService } from '../service/gtm.service';
import { OptionListService } from '../service/optionList.service';
import { QdApiService } from '../service/qd.service';
import { UserDataService } from '../service/userDataService';
import { DepositComponent } from './deposit/deposit.component';
import { OdPopupComponent } from './odPopup/od-popup.component';

@Component({
    selector: 'app-qd',
    templateUrl: './qd.component.html',
    imports: [DepositComponent, OdPopupComponent],
})
export class QDComponent {
    private navigationService = inject(NavigationService);
    depositAvailable: boolean = false;

    constructor(
        public dataService: DataService,
        public userDataService: UserDataService,
        private behaviorData: BehaviorDataService,
        public optionListService: OptionListService,
        public depositService: DepositService,
        private apiService: QdApiService,
        private gtmService: GtmService,
        private geoComply: GeocomplyService,
        private authDetailsStore: AuthDetailsStore,
    ) {
        this.orchestrate();
    }

    orchestrate() {
        this.optionListService.dataLoading.set(true);
        this.depositService.dataLoading.set(true);
        this.userDataService.dataLoading.set(true);
        if (this.authDetailsStore.getAuthDetails().authFlag) {
            this.fetchUserData();
            this.fetchFxRates();
            this.fetchPendingRedeems();
            this.fetchQDData();
        }
    }

    createHiddenIframe(url: string): void {
        const iframe = document.createElement('iframe');
        const cacheBuster = `nocache=${Date.now()}`; // Unique timestamp
        iframe.src = url.includes('?') ? `${url}&${cacheBuster}` : `${url}?${cacheBuster}`;
        iframe.style.display = 'none';
        iframe.onload = () => {
            document.body.removeChild(iframe);
        };
        document.body.appendChild(iframe);
    }

    fetchUserData() {
        this.apiService.get(UrlConstants.USER_DATA_URI).subscribe({
            next: (data) => {
                this.userDataService.userData = data;
                const {
                    br_bank_registration_journey_enabled,
                    br_new_bank_registration_journey_enabled,
                    activeRegisteredbankAccounts,
                    processing_timeout_in_min,
                } = data.configParams || {};
                const pixBrInfo: any = {
                    isSeamless: br_new_bank_registration_journey_enabled == 'Y',
                    isConservative: br_bank_registration_journey_enabled == 'Y',
                    isNewBrLabel: br_bank_registration_journey_enabled == 'Y' || br_new_bank_registration_journey_enabled == 'Y',
                    activeBankAccounts: activeRegisteredbankAccounts,
                    processingTime: processing_timeout_in_min ? processing_timeout_in_min / 60 : 0,
                };
                this.userDataService.userData.configParams.pixBrInfo = pixBrInfo;
                this.userDataService.userData.configParams.isBrazilBankRegEnabled = this.userDataService.isBrazilBankRegEnabled(data?.configParams);
                this.behaviorData.setUserConfig(data);
                if (this.userDataService?.userData?.configParams?.br_is_whitelisted_user == 'Y') {
                    this.optionListService.redirectToMainCashier();
                    return;
                }
                this.gtmService.loadGTMData();
                this.userDataService.dataLoading.set(false);
                if (data?.configParams?.qd_ngpg_iframe_enabled === 'Y') {
                    this.createHiddenIframe(`${data?.configParams?.pgw_frontend_url}/cardIframe/cookieUpdate.html`);
                }
                if (
                    this.userDataService.userData?.configParams?.spainRekycEnabled == 'Y' &&
                    ((this.userDataService.userData?.configParams?.rekycSubErrorCode == '502' &&
                        this.userDataService.userData?.configParams?.spain_rekycFailed == 'true') ||
                        ['501', '503', '504'].includes(this.userDataService.userData?.configParams?.rekycSubErrorCode))
                ) {
                    if (['501', '503', '504'].includes(this.userDataService.userData?.configParams?.rekycSubErrorCode)) {
                        this.navigationService.goTo(this.userDataService.userData?.configParams?.rekycUrl, { forceReloadTarget: 'top' });
                    } else {
                        this.optionListService.redirectToMainCashier('&type=options');
                    }
                }

                if ([AppConstants.CORAL || AppConstants.LADBROKEUK].includes(this.dataService.sessionInfo.brandId)) {
                    this.gtmService.raiseEventForDepositCTA();
                    this.gtmService.raiseEventForDepositCTACashierPopupScreenLoad();
                }
                // spain occupational details
                if (data?.configParams?.isPlayerJobDetailsUpdated == 'N') {
                    this.loadODetails();
                }
            },
            error: (error) => {
                this.dataService.error.set(error);
            },
        });
    }

    fetchFxRates() {
        this.apiService.get(UrlConstants.FX_RATES_URI).subscribe(
            (data) => {
                this.depositService.conversionRates = data;
                this.depositService.dataLoading.set(false);
            },
            (error) => {
                this.dataService.error.set(error);
            },
        );
    }
    loadODetails() {
        const lang = this.dataService?.sessionInfo.langId || 'en';
        this.apiService.get(`${UrlConstants.OCCUPATIONAL_DETAILS}?lang=${lang}`).subscribe((data) => {
            this.behaviorData.setODDetails(data);
            if (data?.softLaunch && ['remind-later'].includes(this.behaviorData.userODetails?.status)) {
                this.depositService.showODpopUp.set(true);
            } else if (!data?.softLaunch && ['block-payment'].includes(this.behaviorData.userODetails?.status)) {
                this.optionListService.redirectToMainCashier();
            }
        });
    }

    fetchPendingRedeems() {
        this.apiService.get(UrlConstants.REVERSALS_URI).subscribe(
            (data) => {
                if (data?.redeemInfoList?.length > 0) {
                    this.optionListService.reversalExists = true;
                    this.optionListService.reversalArr = data?.redeemInfoList;
                }
            },
            (error) => {
                this.dataService.error.set(error);
            },
        );
    }

    fetchQDData() {
        this.apiService.get(UrlConstants.QD_INSTRUMENTS_URI).subscribe(
            (data) => {
                if (Array.isArray(data) && data.length > 0) {
                    this.optionListService.optionList = data;
                    this.optionListService.selectedOption = data[0];
                    const isBelgiumRgDecreeEnabled = this.userDataService?.userData?.configParams?.belgium_rg_decree_enabled === Config.YES;
                    if (isBelgiumRgDecreeEnabled) {
                        this.getRgLimitData();
                    }
                    this.depositAvailable = true;

                    this.optionListService.optionList.map((item) => {
                        if (
                            (item.paymentMethodInfo?.paymentMethod === InputType.PAYPAL &&
                                item.paymentAccountInfo?.paypalSportsOnlyLogo === Config.YES) ||
                            (item.paymentMethodInfo?.paymentMethod === InputType.VISA && item.paymentAccountInfo?.visaSportsOnlyLogo === Config.YES)
                        ) {
                            item.sportsOnly = true;
                        } else if (
                            item.paymentMethodInfo?.paymentMethod === InputType.PAYPAL &&
                            item.paymentAccountInfo?.paypalBankAccountsOnlyLogo === Config.YES
                        ) {
                            item.bankAccountsOnly = true;
                        }
                    });

                    this.optionListService.optionList = this.filterOptionList();

                    if (this.optionListService?.optionList?.length > 3) {
                        this.optionListService.optionList = this.optionListService?.optionList?.splice(0, 3);
                    }
                    if (this.optionListService?.optionList?.length === 0) {
                        this.optionListService.redirectToMainCashier();
                    } else if (
                        this.optionListService?.optionList?.length === 1 &&
                        this.optionListService?.optionList[0]?.redirectToInputPage &&
                        [InputType.PLAYMGMPREPAIDCARD, InputType.BORGATAPREPAIDCARD, InputType.PARTYPREPAIDCARD, InputType.INSTANTECHECKS].includes(
                            this.optionListService?.optionList[0]?.paymentMethodInfo?.paymentMethod,
                        )
                    ) {
                        this.optionListService.redirectToMainCashier();
                    }

                    this.depositService.depositInfo.amountInfo.nativeCurrency =
                        this.optionListService?.selectedOption?.paymentMethodInfo?.minDepositLimit?.currencyCode;
                    this.depositService.depositInfo.amountInfo.nativeAmountString = this.depositService.getAmountInTransactionCurrency();
                    this.optionListService.dataLoading.set(false);
                    const paymentMethod = this.optionListService?.selectedOption?.paymentMethodInfo?.paymentMethod;
                    this.gtmService.raiseEvent('quickDepositLoad', paymentMethod?.toLowerCase() + 'Input', paymentMethod, true);
                    this.geoComplyInit();
                } else {
                    this.depositAvailable = false;
                    this.optionListService.dataLoading.set(false);
                    this.optionListService.redirectToMainCashier('&type=deposit');
                    return;
                }
            },
            (error) => {
                this.dataService.error.set(error);
            },
        );
    }
    geoComplyInit() {
        const geoComplyLabel: string | undefined = this.userDataService?.userData?.configParams?.GEOCOMPLY_LABEL_NAME;
        const userData = this.userDataService?.userData;
        const channelId = userData?.userInfo?.labelInfo?.channelId || '';
        if (userData?.configParams?.merchant_hostname && geoComplyLabel && !MOBILE_APP_CHANNELS.includes(channelId)) {
            this.geoComply.loadGeoComplyScript(this.userDataService?.userData);
        }
    }

    filterOptionList(): QDInstrument[] {
        const { paypalOnlyForSports, visaOnlyForSports, enable_prepaidcard_restrictions, rollingweek_remaining_balance } =
            this.userDataService?.userData?.configParams || {};
        const rollingWeekRemainingAmount = Number(rollingweek_remaining_balance);
        const enablePrepaidcardRestrictions = enable_prepaidcard_restrictions === 'Y';
        return this.optionListService.optionList.filter((usedOption) => {
            const rollingWeekLimitExcceded =
                rollingWeekRemainingAmount == 0 || Number(usedOption?.paymentMethodInfo?.minDepositLimit?.value) > rollingWeekRemainingAmount;

            if (enablePrepaidcardRestrictions && rollingWeekLimitExcceded && usedOption?.fundingSource?.toLowerCase() === AppConstants.PREPAID) {
                return false;
            }
            if ([InputType.APPLEPAY, InputType.APPLEPAYDIRECT].includes(usedOption?.paymentMethodInfo?.paymentMethod)) {
                const params = new URLSearchParams(window?.location?.search);
                const launchSource = params.get('launchSource');
                return launchSource?.toUpperCase() === 'GAMES' ? false : this.isIOS17OrGreater() || this.getApplePayFilter();
            }
            if (paypalOnlyForSports === Config.YES || visaOnlyForSports === Config.YES) {
                if (this.dataService?.sessionInfo?.walletBucketType === InputType.PAYPAL) {
                    return (
                        (paypalOnlyForSports === Config.YES && usedOption?.paymentMethodInfo?.paymentMethod === InputType.PAYPAL) ||
                        (visaOnlyForSports === Config.YES && usedOption?.paymentMethodInfo?.paymentMethod === InputType.VISA)
                    );
                } else if (this.dataService?.sessionInfo?.walletBucketType === InputType.NONPAYPAL) {
                    if (paypalOnlyForSports === Config.YES && usedOption?.paymentMethodInfo?.paymentMethod === InputType.PAYPAL) {
                        return false;
                    } else if (visaOnlyForSports === Config.YES && usedOption?.paymentMethodInfo?.paymentMethod === InputType.VISA) {
                        return false;
                    } else {
                        return true;
                    }
                } else if (usedOption?.paymentMethodInfo?.paymentMethod === InputType.APPLEPAY) {
                    return false;
                } else {
                    if ([ProductType.SPORTSBOOK, ProductType.BETTING].includes(this.dataService?.sessionInfo?.productId)) {
                        return true;
                    } else {
                        if (paypalOnlyForSports === Config.YES && usedOption?.paymentMethodInfo?.paymentMethod === InputType.PAYPAL) {
                            return false;
                        } else if (visaOnlyForSports === Config.YES && usedOption?.paymentMethodInfo?.paymentMethod === InputType.VISA) {
                            return false;
                        } else {
                            return true;
                        }
                    }
                }
            } else {
                return true;
            }
        });
    }

    getRgLimitData() {
        this.apiService.get(UrlConstants.GET_RG_LIMIT_INFO).subscribe((response) => {
            if (response.length > 0) {
                const inputData: any = {};
                inputData.rglimitInfo = response;
                const rgbucketlimits = this.userDataService?.userData?.configParams?.rg_bucket_threshold_limits.split(',');
                if (rgbucketlimits.length > 0) {
                    inputData.rg_first_bucket_threshold = rgbucketlimits[0];
                    inputData.rg_second_bucket_threshold = rgbucketlimits[1];
                    inputData.rg_third_bucket_threshold = rgbucketlimits[2];
                }
                this.dataService.RgLimitData = inputData;
            }
        });
    }
    isIOS17OrGreater() {
        const iosVersionPattern = /iPhone OS (\d+)_/;
        const match = navigator.userAgent.match(iosVersionPattern);
        if (match && match[1]) {
            const iosVersion = parseInt(match[1], 10);
            return iosVersion >= 17;
        }
        return false;
    }

    getApplePayFilter() {
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        if (!isSafari) return false;

        const versionMatch = navigator.userAgent.match(/Version\/(\d+\.\d+)/);
        return versionMatch ? parseInt(versionMatch[1] ?? '0') >= 17 : false;
    }
}
